import type {GetStaticProps} from 'next/types';
import Head from 'next/head';
import {Container, Flex, Text} from '@chakra-ui/layout';
import {QueryClient, dehydrate} from '@tanstack/react-query';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

import {PlayCard} from '@eksab/features/play/components/PlayCard';
import {useGamesCountQuery} from '@eksab/features/play/hooks/useGamesCountQuery';
import groupsIcon from '@eksab/features/play/icons/groups-icon.png';

export default function Play() {
  const t = useTranslate();
  const regionSettingsQuery = useRegionSettings();
  const gamesCountQuery = useGamesCountQuery(regionSettingsQuery.data?.settings.active_games.map((game) => game.name));

  return (
    <>
      <Head>
        <meta property="og:title" content={`${t('play')} | ${t('app.name')}`} />
        <title>{`${t('play')} | ${t('app.name')}`}</title>
      </Head>

      <Container as="section" flexDir="column" gap={[6, 7, 8]} py={py}>
        <Flex as="header" flexDir="column" justify="center" isolation="isolate">
          <Text as="h1" fontSize={['xl', '2xl', '3xl']} fontWeight="700">
            {t('play.title')}
          </Text>

          <Text color="gray.300" fontSize={['sm', 'md', 'lg']} fontWeight="500" maxW="65%">
            {t('play.description')}
          </Text>
        </Flex>

        <Flex flexDir="column" gap={[3, null, 4]}>
          {regionSettingsQuery.data?.settings.active_games.map((game) => (
            <PlayCard
              key={game.name}
              name={game.name}
              image={game.logo}
              route={game.route}
              count={gamesCountQuery.data?.[game.name]}
            />
          ))}

          {regionSettingsQuery.data?.settings.show_groups && (
            <PlayCard name="groups" image={groupsIcon} route="groups" count={null} />
          )}
        </Flex>
      </Container>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({locale}) => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60 * 60 * 24,
  };
};
